import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, ShoppingCart, Heart, User, Menu, X, LayoutDashboard, ChevronDown, ArrowLeft, TrendingUp } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useCart } from '@/context/CartContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SITE_CATEGORIES } from '@/constants/categories';

export const Header = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchDrawerOpen, setSearchDrawerOpen] = useState(false); // New State for Search Drawer
  const searchInputRef = useRef(null);

  const { user, logout } = useAuth();
  const { cartCount, wishlist } = useCart();
  const navigate = useNavigate();

  // Focus input when drawer opens
  useEffect(() => {
    if (searchDrawerOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [searchDrawerOpen]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${searchQuery}`);
      setSearchDrawerOpen(false);
      setMobileMenuOpen(false);
      setSearchQuery('');
    }
  };

  return (
    <>
      <header className="sticky top-0 z-40 w-full bg-white border-b border-border shadow-sm font-outfit">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 gap-3">

            {/* 1. Mobile Menu Button */}
            <button
              className="lg:hidden p-2 -ml-2 text-gray-600 hover:text-black"
              onClick={() => setMobileMenuOpen(true)}
            >
              <Menu className="w-6 h-6" />
            </button>

            {/* 2. Logo */}
            <Link to="/" className="font-bold text-2xl tracking-tight flex-shrink-0 text-yellow-500">
              BuggyBerry
            </Link>

            {/* 3. Desktop Mega Menu */}
            <div className="hidden lg:flex items-center space-x-6 h-full ml-6 flex-1">
              <div className="group relative h-full flex items-center">
                <button className="flex items-center text-sm font-medium hover:text-yellow-600 h-full border-b-2 border-transparent hover:border-yellow-500 transition-all">
                  Categories <ChevronDown className="ml-1 w-4 h-4" />
                </button>
                {/* Mega Menu Dropdown */}
                <div className="absolute top-full left-[-20px] w-[800px] bg-white shadow-2xl border-t border-gray-100 rounded-b-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 transform translate-y-2 group-hover:translate-y-0 grid grid-cols-3 gap-8 p-8 z-50">
                  {SITE_CATEGORIES.map((cat) => (
                    <div key={cat.id}>
                      <h3 className="font-bold text-gray-900 mb-3 flex items-center border-b pb-2">
                        <span className="mr-2">{cat.emoji}</span>
                        <Link to={`/products?gender=${cat.id}`} className="hover:text-yellow-600">{cat.label}</Link>
                      </h3>
                      <ul className="space-y-2">
                        {cat.subcategories.map((sub) => (
                          <li key={sub.name}>
                            <Link to={`/products?gender=${cat.id}&category=${sub.value}`} className="text-sm text-gray-500 hover:text-yellow-600 block">
                              {sub.name}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
              <Link to="/products?trending=true" className="text-sm font-medium hover:text-yellow-600">Trending</Link>
              <Link to="/products?is_new=true" className="text-sm font-medium hover:text-yellow-600">New Arrivals</Link>
            </div>

            {/* 4. Desktop Search */}
            <form onSubmit={handleSearch} className="hidden lg:flex max-w-xs w-full mr-4">
              <div className="relative w-full group">
                <Input
                  type="text"
                  placeholder="Search by product, category or collection..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-4 pr-10 rounded-full bg-gray-100 border-transparent focus:bg-white focus:border-yellow-400 h-10 text-sm"
                />
                <button type="submit" className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-yellow-600">
                  <Search className="w-4 h-4" />
                </button>
              </div>
            </form>

            {/* 5. Icons (Search Trigger, Wishlist, Cart, User) */}
            <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">

              {/* Mobile Search Trigger (Magnifying Glass) */}
              <button
                className="lg:hidden p-2 text-gray-700"
                onClick={() => setSearchDrawerOpen(true)}
              >
                <Search className="w-5 h-5" />
              </button>

              <Link to="/wishlist" className="relative p-2 hover:bg-yellow-50 rounded-full transition-colors text-gray-700">
                <Heart className="w-5 h-5" />
                {wishlist.length > 0 && (
                  <span className="absolute top-0 right-0 bg-red-500 text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center font-bold">
                    {wishlist.length}
                  </span>
                )}
              </Link>

              <Link to="/cart" className="relative p-2 hover:bg-yellow-50 rounded-full transition-colors text-gray-700">
                <ShoppingCart className="w-5 h-5" />
                {cartCount > 0 && (
                  <span className="absolute top-0 right-0 bg-yellow-500 text-black text-[10px] w-4 h-4 rounded-full flex items-center justify-center font-bold">
                    {cartCount}
                  </span>
                )}
              </Link>

              {/* User Icon (NOW VISIBLE ON MOBILE) */}
              {user ? (
                <div className="relative group ml-1">
                  <Link to="/profile" className="flex items-center justify-center w-8 h-8 bg-gray-100 rounded-full hover:bg-yellow-100 transition-colors">
                    <User className="w-5 h-5 text-gray-700" />
                  </Link>
                  {/* Desktop Hover Dropdown */}
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-gray-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 p-2 hidden lg:block">
                    <div className="px-4 py-2 border-b border-gray-100 mb-2">
                      <p className="font-semibold text-sm truncate">{user.name}</p>
                      <p className="text-sm text-gray-500 mb-3">{user.email}</p>
                    </div>
                    {user.isAdmin && (
                      <Link to="/admin" className="flex items-center px-4 py-2 text-sm hover:bg-yellow-50 rounded-lg mb-1 font-medium text-amber-600">
                        <LayoutDashboard className="w-4 h-4 mr-2" /> Admin Panel
                      </Link>
                    )}
                    <Link to="/orders" className="block px-4 py-2 text-sm hover:bg-gray-50 rounded-lg">My Orders</Link>
                    <button onClick={logout} className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg mt-1">Logout</button>
                  </div>
                </div>
              ) : (
                <Link to="/login" className="p-2">
                  <User className="w-5 h-5 text-gray-700" />
                </Link>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* --- MOBILE SEARCH DRAWER (Bewakoof Style) --- */}
      {searchDrawerOpen && (
        <div className="fixed inset-0 z-50 bg-white flex flex-col animate-in slide-in-from-bottom-5 duration-300">

          {/* Header with Back Button & Input */}
          <div className="flex items-center gap-3 p-4 border-b border-gray-100 shadow-sm">
            <button onClick={() => setSearchDrawerOpen(false)} className="text-gray-500">
              <ArrowLeft className="w-6 h-6" />
            </button>
            <form onSubmit={handleSearch} className="flex-1">
              <input
                ref={searchInputRef}
                type="text"
                className="w-full text-lg outline-none placeholder:text-gray-400 font-medium"
                placeholder="Type to search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </form>
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} className="text-gray-400">
                <X className="w-5 h-5" />
              </button>
            )}
          </div>

          {/* Popular Categories (Sidebar Content) */}
          <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4">Popular Categories</h3>

            <div className="space-y-6">
              {SITE_CATEGORIES.map((cat) => (
                <div key={cat.id} className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                  <h4 className="font-bold text-gray-900 mb-3 flex items-center">
                    <span className="mr-2 text-xl">{cat.emoji}</span> {cat.label}
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {cat.subcategories.map((sub) => (
                      <button
                        key={sub.name}
                        onClick={() => {
                          navigate(`/products?gender=${cat.id}&category=${sub.value}`);
                          setSearchDrawerOpen(false);
                        }}
                        className="px-3 py-2 bg-gray-50 hover:bg-yellow-50 border border-gray-200 hover:border-yellow-400 rounded-lg text-sm text-gray-600 hover:text-black transition-all"
                      >
                        {sub.name}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* --- MOBILE HAMBURGER MENU --- */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 flex lg:hidden">
          {/* Backdrop */}
          <div className="fixed inset-0 bg-black/50" onClick={() => setMobileMenuOpen(false)}></div>

          {/* Sidebar */}
          <div className="relative w-[80%] max-w-sm bg-white h-full shadow-2xl flex flex-col animate-in slide-in-from-left duration-300">

            {/* Header */}
            <div className="p-4 border-b flex justify-between items-center bg-gray-50">
              <h2 className="font-bold text-lg text-yellow-600">BuggyBerry</h2>
              <button onClick={() => setMobileMenuOpen(false)} className="p-1 bg-white rounded-full shadow-sm"><X className="w-5 h-5" /></button>
            </div>

            {/* User Section */}
            {user ? (
              <div className="p-4 bg-yellow-50/50 border-b border-yellow-100">
                <p className="font-bold text-lg">Hi, {user.name}</p>
                <p className="text-sm text-gray-500 mb-3">{user.email}</p>
                <div className="flex gap-2">
                  <Link to="/orders" onClick={() => setMobileMenuOpen(false)} className="text-xs flex text-center items-center bg-white border px-3 py-1.5 rounded-full font-medium shadow-sm">My Orders</Link>
                  <Link to="/wishlist" onClick={() => setMobileMenuOpen(false)} className="text-xs flex text-center items-center bg-white border px-3 py-1.5 rounded-full font-medium shadow-sm">Wishlist</Link>
                  {user.isAdmin && (
                    <Link to="/admin" className="text-xs flex text-center items-center bg-white border px-3 py-1.5 rounded-full font-medium shadow-sm text-amber-600">
                       Admin Panel
                    </Link>
                  )}
                </div>
              </div>
            ) : (
              <div className="p-4 border-b">
                <p className="text-gray-500 text-sm mb-3">Login to access your orders</p>
                <Button onClick={() => { navigate('/login'); setMobileMenuOpen(false); }} className="w-full bg-yellow-400 text-black font-bold">Login / Sign Up</Button>
              </div>
            )}

            {/* Navigation Links */}
            <nav className="flex-1 overflow-y-auto p-4 space-y-6">

              {/* Added: Quick Links (Missing in old mobile menu) */}
              <div className="grid grid-cols-2 gap-3">
                <Link
                  to="/products?trending=true"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center justify-center p-3 bg-red-50 text-red-600 rounded-lg text-sm font-bold border border-red-100"
                >
                  <TrendingUp className="w-4 h-4 mr-1" /> Trending
                </Link>
                <Link
                  to="/products?is_new=true"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center justify-center p-3 bg-blue-50 text-blue-600 rounded-lg text-sm font-bold border border-blue-100"
                >
                  New Arrivals
                </Link>
              </div>

              {/* Categories */}
              {SITE_CATEGORIES.map(cat => (
                <div key={cat.id}>
                  <h3 className="font-bold text-gray-900 mb-3 flex items-center">
                    <span className="mr-2 bg-gray-100 p-1 rounded text-sm">{cat.emoji}</span> {cat.label}
                  </h3>
                  <div className="pl-8 space-y-2 border-l-2 border-gray-100 ml-3">
                    <Link
                      to={`/products?gender=${cat.id}`}
                      onClick={() => setMobileMenuOpen(false)}
                      className="block text-sm font-semibold text-black mb-2"
                    >
                      View All
                    </Link>
                    {cat.subcategories.map(sub => (
                      <Link
                        key={sub.name}
                        to={`/products?gender=${cat.id}&category=${sub.value}`}
                        onClick={() => setMobileMenuOpen(false)}
                        className="block text-sm text-gray-500 py-1"
                      >
                        {sub.name}
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
            </nav>

            {/* Logout */}
            {user && (
              <div className="p-4 border-t">
                <button onClick={() => { logout(); setMobileMenuOpen(false); }} className="text-red-600 text-sm font-bold">Logout</button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};